'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo } from 'react';
import { CheckboxWithLabel } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { DATE_RANGE_KEYS, getReleaseDateController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { ReleaseDateFacetProps } from './ReleaseDateFacet.types';

export const ReleaseDateFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: ReleaseDateFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const dateLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.release-date.title')
      ?.value ?? 'Release Date';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.release-date.clear')
      ?.value ?? 'Clear';

  const eng = useEngine();
  const controller = useMemo(() => getReleaseDateController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    'first_published_at',
    eng
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const getLabel = (index: number) => {
    const range = DATE_RANGE_KEYS[index];
    const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
    return entry?.value ?? range.label;
  };

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      {optimisticValues.map((facetValue, index) => (
        <CheckboxWithLabel
          key={`release-date-${facetValue.start}-${facetValue.end}`}
          id={`release-date-${index}`}
          checked={facetValue.state === 'selected'}
          disabled={
            facetValue.numberOfResults === 0 && facetValue.state !== 'selected'
          }
          onCheckedChange={() => {
            const stableValue = findStableValue(facetValue);
            if (!stableValue) return;
            applyOptimistic([stableValue], () =>
              controller.toggleSelect(stableValue)
            );
            controller.toggleSelect(stableValue);
          }}
          count={facetValue.numberOfResults}
          size="sm"
          isDarkTheme={isDark}
          microcopy={{ label: getLabel(index) }}
        />
      ))}
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={dateLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={dateLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
